import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { FileText } from 'lucide-react';
import type { PassportRegistryEntry } from '@siliconx/shared';
import { PASSPORT_REGISTRY_SECTION_LABELS, PASSPORT_REGISTRY_SECTIONS } from '@siliconx/shared';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { cn } from '@siliconx/shared/ui/utils';
import { api, throwIfNotOk } from '../lib/api';
import { formatDate } from '../lib/format';
import { qk } from '../lib/queryKeys';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-575 (I2-D3, wayfinder-iter2-spec.md §5, §7-D3) — Digital Passports
 * registry, Intelligence › Outcomes & Reports. A fleet-wide list over the
 * per-asset passports that already exist at /passport/:fingerprint (the
 * print surface, RUF-555) — no new passport content of its own, purely a
 * read model (GET /api/client/assets/passports, SIL-575) surfacing which
 * §-sections of each asset's passport currently have real data behind them.
 */
// SIL-1108: `orgFeature: 'marketplaceOps'` (customerNav.ts) is
// business-only — gated here so a direct hit doesn't fire this page's
// mount-time fetch before the root `OrgFeatureGate`'s (__root.tsx) redirect
// lands.
export const Route = createFileRoute('/passports')({
  beforeLoad: () => requireOrgFeature('marketplaceOps'),
  component: DigitalPassportsPage,
});

function CompletenessBadge({ entry }: { entry: PassportRegistryEntry }) {
  const { populated, total } = entry.completeness;
  const complete = populated === total;
  const empty = populated === 0;
  return (
    <span
      title={PASSPORT_REGISTRY_SECTIONS.map(
        (s) => `${entry.sections.includes(s) ? '✓' : '·'} ${PASSPORT_REGISTRY_SECTION_LABELS[s]}`,
      ).join('\n')}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        complete
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : empty
            ? 'bg-muted text-muted-foreground ring-border'
            : 'bg-amber-50 text-amber-700 ring-amber-200',
      )}
    >
      {populated} / {total} sections
    </span>
  );
}

function columns(onOpen: (assetId: string) => void): ColumnDef<PassportRegistryEntry, unknown>[] {
  return [
    {
      id: 'asset',
      header: 'Asset',
      cell: (ctx) => {
        const row = ctx.row.original;
        const title =
          row.manufacturerName && row.skuModelName
            ? `${row.manufacturerName} ${row.skuModelName}`
            : (row.serialNumber ?? row.assetId.slice(0, 8));
        return (
          <div>
            <div className="font-medium text-foreground">{title}</div>
            {row.serialNumber && <div className="text-xs text-muted-foreground">{row.serialNumber}</div>}
          </div>
        );
      },
    },
    {
      id: 'class',
      header: 'Class / SKU',
      cell: (ctx) => (
        <span className="text-foreground">
          {ctx.row.original.assetClassName ?? '—'}
          {ctx.row.original.skuModelName ? ` · ${ctx.row.original.skuModelName}` : ''}
        </span>
      ),
    },
    {
      id: 'lifecycle',
      header: 'Lifecycle',
      cell: (ctx) => <span className="text-foreground">{ctx.row.original.lifecycleStateName ?? '—'}</span>,
    },
    {
      id: 'completeness',
      header: 'Passport completeness',
      cell: (ctx) => <CompletenessBadge entry={ctx.row.original} />,
    },
    {
      id: 'lastUpdated',
      header: 'Last updated',
      cell: (ctx) => (
        <span className="text-muted-foreground">{formatDate(ctx.row.original.lastUpdated)}</span>
      ),
    },
    {
      id: 'view',
      header: () => <span className="sr-only">View</span>,
      cell: (ctx) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(ctx.row.original.fingerprint);
          }}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          <FileText className="size-3.5" /> View passport
        </button>
      ),
    },
  ];
}

function DigitalPassportsPage() {
  const navigate = useNavigate();

  const registryQ = useQuery<{ data: PassportRegistryEntry[] }>({
    queryKey: qk.assets.passportRegistry(),
    queryFn: async () => {
      const res = await api.api.client.assets.passports.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: PassportRegistryEntry[] };
    },
  });

  const openPassport = (fingerprint: string) => {
    void navigate({ to: '/passport/$fingerprint', params: { fingerprint } });
  };

  const rows = registryQ.data?.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Digital Passports"
        pinTo="/passports"
        subtitle="Fleet-wide registry of every asset's digital passport — completeness reflects the real telemetry, FMV, and carbon data behind each one."
      />

      {registryQ.isLoading && <LoadingShell rows={6} />}
      {registryQ.isError && (
        <ErrorState
          title="Could not load the Digital Passports registry"
          error={registryQ.error}
          onRetry={() => void registryQ.refetch()}
        />
      )}

      {registryQ.data &&
        (rows.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">No assets with a digital passport yet.</p>
          </div>
        ) : (
          <DataTable
            data={rows}
            columns={columns(openPassport)}
            onRowClick={(row: PassportRegistryEntry) => openPassport(row.assetId)}
          />
        ))}
    </div>
  );
}
